import { Card } from "./ui/card";
import { Users, Heart, MessageCircle, Shield } from "lucide-react";
import ComicTitle from './ui/ComicTitle';
import drawing5 from '../assets/drawing5.png';
import drawing6 from '../assets/drawing6.png';
import drawing7 from '../assets/drawing7.png';
import drawing8 from '../assets/drawing8.png';
import drawing9 from '../assets/drawing9.png';
import drawing10 from '../assets/drawing10.png';
import { useState, useEffect } from "react";

export const About = () => {
    const features = [
        {
            icon: Users,
            title: "Student-Centered",
            description: "Designed for high school students with their real experiences and needs in mind.",
            color: "#38bdf8",
            accent: "bg-sky-400",
            tilt: "-rotate-[1.2deg]",
        },
        {
            icon: Heart,
            title: "Empathy Building",
            description: "Encourages deeper understanding, compassion, and connection through shared dialogue.",
            color: "#c084fc",
            accent: "bg-violet-400",
            tilt: "rotate-[1deg]",
        },
        {
            icon: MessageCircle,
            title: "Real Conversations",
            description: "Opens authentic dialogue that leads to meaningful support, reflection, and change.",
            color: "#fb923c",
            accent: "bg-orange-400",
            tilt: "-rotate-[0.8deg]",
        },
        {
            icon: Shield,
            title: "Safe Space",
            description: "Creates a setting where students can share, listen, learn, and grow together.",
            color: "#f472b6",
            accent: "bg-pink-400",
            tilt: "rotate-[1.1deg]",
        },
    ];

    return (
        <section
            id="about"
            className="relative overflow-hidden bg-cover bg-center py-12 text-white"
            style={{ backgroundImage: "url('/assets/paper_1.jpg')" }}
        >
            {/* dark overlay */}
            <div className="absolute inset-0 bg-yellow-200/20" />

            {/* halftone tint */}
            <div className="absolute inset-0 bg-[radial-gradient(circle,rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:12px_12px] opacity-20" />

            <div className="container relative z-10 mx-auto px-4">
                <div className="mx-auto max-w-6xl">
                    {/* Section Header */}
                    <div className="mb-20 text-center">
                      <div className="flex flex-wrap items-center justify-center gap-3 text-center">
                          {/* "What is" */}
                          <span
                              className="text-7xl md:text-8xl font-black uppercase text-black"
                              style={{
                                  textShadow: "3px 3px 0 #fff",
                                  letterSpacing: "2px",
                              }}
                          >
                              What is
                          </span>

                          {/* IMAGE */}
                          <img
                              src="/assets/CIO_2.png"
                              alt="Call It Out"
                              className="h-[60px] md:h-[84px] w-auto"
                          />

                          {/* "?" */}
                          <span
                              className="text-6xl md:text-7xl font-black uppercase text-black"
                              style={{
                                  textShadow: "3px 3px 0 #000",
                              }}
                          >
                              ?
                          </span>
                      </div>

                        <div className="mx-auto mb-8 mt-3 h-2 w-64 rounded-full bg-gradient-to-r from-red-500 via-yellow-300 to-cyan-400 shadow-[0_2px_0_#000]" />

                        <div className="mx-auto max-w-4xl rounded-[28px] border-[4px] border-black bg-[#ffffff] px-6 py-6 text-black shadow-[8px_8px_0_#000]">
                            <p className="text-lg font-semibold leading-9 md:text-xl">
                                <span className="font-bold text-red-600">
                                  Call It Out transforms the
                                  conversation around bullying.
                                </span> With 10 engaging categories,
                                students explore personal experiences, practice standing up for
                                themselves and others, and build new narratives of connection,
                                empathy, and respect.
                            </p>
                        </div>
                    </div>

                    {/* Features Grid */}
                    <div className="mb-4 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                        {features.map((feature, index) => (
                            <div
                                key={index}
                                className={`group relative ${feature.tilt} transition-transform duration-300 hover:-translate-y-2 hover:rotate-0`}
                            >
                                {/* accent tab */}
                                <div
                                    className={`absolute -top-3 left-6 h-4 w-20 rounded-t-md border-[3px] border-b-0 border-black ${feature.accent}`}
                                />

                                <Card className="relative h-full rounded-[22px] border-[4px] border-black bg-[#f5f5f5] p-6 text-black shadow-[8px_8px_0_#000] transition-all duration-300">
                                    {/* corner burst */}
                                    <div
                                        className="absolute -right-3 -top-3 flex h-9 w-9 items-center justify-center rounded-full border-[3px] border-black bg-yellow-300 text-xs font-black text-black"
                                    >
                                        !
                                    </div>

                                    <div
                                        className="mb-5 flex h-14 w-14 items-center justify-center rounded-[16px] border-[3px] border-black bg-white shadow-[3px_3px_0_#000]"
                                    >
                                        <feature.icon
                                            className="h-7 w-7"
                                            style={{ color: feature.color }}
                                        />
                                    </div>

                                    <h3 className="mb-3 text-[1.05rem] uppercase leading-tight tracking-wide text-black">
                                        {feature.title}
                                    </h3>

                                    <p className="text-md font-semibold leading-8 text-black/85">
                                        {feature.description}
                                    </p>
                                </Card>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};